import { useRef, useCallback, useEffect, useMemo } from 'react';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

/**
 * Tracks all Firestore read operations during a user session.
 * Persists a summary to Firestore on logout/unload for analysis.
 * 
 * Usage:
 *   const { trackRead, printSummary, flushSession } = useSessionTracker(user?.email);
 *   trackRead('fetchMonthlyStats', 'monthly_stats', stats.length);
 *   // On logout: await flushSession();
 *   // In dev console: window.__sessionTracker.printSummary()
 */
export const useSessionTracker = (userEmail) => {
  const sessionId = useRef(`${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const sessionStart = useRef(Date.now());
  const readLog = useRef([]); // Array of { source, collection, docCount, timestamp, ...metadata }
  const isFlushed = useRef(false);

  /**
   * Record a Firestore read operation.
   * @param {string} source - The function/component that triggered the read (e.g., 'fetchPage', 'getMonthlyStats')
   * @param {string} collectionName - The Firestore collection read from (e.g., 'bills', 'monthly_stats')
   * @param {number} docCount - Number of documents read in this operation
   * @param {object} [metadata] - Optional extra info (filters, page number, etc.)
   */
  const trackRead = useCallback((source, collectionName, docCount, metadata = {}) => {
    const entry = {
      source,
      collection: collectionName,
      docCount,
      timestamp: Date.now(),
      ...metadata
    };
    readLog.current.push(entry);

    // Log to console in dev for real-time visibility
    if (import.meta.env.DEV) {
      logger.log(`[SESSION-TRACK] ${source} -> ${collectionName}: ${docCount} docs`, metadata);
    }
  }, []);

  /**
   * Build a summary of all reads grouped by source function.
   */
  const getSummary = useCallback(() => {
    const bySource = {};
    const byCollection = {};
    let totalReads = 0;

    readLog.current.forEach(entry => {
      // Group by source
      if (!bySource[entry.source]) {
        bySource[entry.source] = { calls: 0, totalDocs: 0, collections: {} };
      }
      bySource[entry.source].calls += 1;
      bySource[entry.source].totalDocs += entry.docCount;

      if (!bySource[entry.source].collections[entry.collection]) {
        bySource[entry.source].collections[entry.collection] = 0;
      }
      bySource[entry.source].collections[entry.collection] += entry.docCount;

      // Group by collection
      if (!byCollection[entry.collection]) {
        byCollection[entry.collection] = { calls: 0, totalDocs: 0 };
      }
      byCollection[entry.collection].calls += 1;
      byCollection[entry.collection].totalDocs += entry.docCount;

      totalReads += entry.docCount;
    });

    return {
      sessionId: sessionId.current,
      userEmail,
      sessionStart: sessionStart.current,
      sessionDuration: Date.now() - sessionStart.current,
      totalReads,
      totalOperations: readLog.current.length,
      bySource,
      byCollection,
      // Include the top 10 heaviest individual operations
      heaviestOps: [...readLog.current]
        .sort((a, b) => b.docCount - a.docCount)
        .slice(0, 10)
        .map(({ source, collection, docCount, timestamp }) => ({
          source, collection, docCount,
          relativeTime: `+${((timestamp - sessionStart.current) / 1000).toFixed(1)}s`
        }))
    };
  }, [userEmail]);

  /**
   * Persist the session summary to Firestore for later analysis.
   * Called on logout or page unload.
   */
  const flushSession = useCallback(async () => {
    if (isFlushed.current || readLog.current.length === 0) return;
    isFlushed.current = true;

    const summary = getSummary();

    try {
      await setDoc(doc(db, 'session_logs', sessionId.current), {
        ...summary,
        sessionStartISO: new Date(summary.sessionStart).toISOString(),
        sessionEndISO: new Date().toISOString(),
        sessionDurationMin: parseFloat((summary.sessionDuration / 60000).toFixed(2)),
        flushedAt: Timestamp.now(),
        // Auto-expire after 30 days (use with TTL policy or manual cleanup)
        expiresAt: Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))
      });
      logger.log(`[SESSION-TRACK] Session flushed: ${summary.totalReads} total reads across ${summary.totalOperations} operations`);
    } catch (error) {
      logger.error('[SESSION-TRACK] Failed to flush session:', error);
    }
  }, [getSummary]);

  /**
   * Print a live summary to the console (for debugging).
   */
  const printSummary = useCallback(() => {
    const summary = getSummary();
    console.group('📊 Session Read Summary');
    console.log(`Total Reads: ${summary.totalReads} | Operations: ${summary.totalOperations}`);
    console.log(`Duration: ${(summary.sessionDuration / 1000).toFixed(0)}s`);
    console.table(
      Object.entries(summary.bySource).map(([source, data]) => ({
        Source: source,
        Calls: data.calls,
        'Total Docs': data.totalDocs,
        'Avg Docs/Call': (data.totalDocs / data.calls).toFixed(1),
        Collections: Object.keys(data.collections).join(', ')
      }))
    );
    console.log('Heaviest Operations:', summary.heaviestOps);
    console.groupEnd();
    return summary;
  }, [getSummary]);

  // Flush on page unload (backup - best effort)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (readLog.current.length > 0 && !isFlushed.current) {
        // Use sendBeacon with a Firestore REST write as a fallback
        // This won't always work but is best-effort for tab closes
        try {
          const summary = getSummary();
          const blob = new Blob([JSON.stringify(summary)], { type: 'application/json' });
          navigator.sendBeacon?.(`https://firestore.googleapis.com/v1/projects/lift-ehs/databases/(default)/documents/session_logs_beacon`, blob);
        } catch (_e) {
          // Silent fail - session data is lost if unload happens without logout
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [getSummary]);

  // Reset when user changes
  useEffect(() => {
    if (userEmail) {
      sessionId.current = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStart.current = Date.now();
      readLog.current = [];
      isFlushed.current = false;
    }
  }, [userEmail]);

  return useMemo(() => ({
    trackRead,
    getSummary,
    printSummary,
    flushSession
  }), [trackRead, getSummary, printSummary, flushSession]);
};
